import { z } from "zod";
import { claimCodeSchema } from "./ranking";
import { slideTypeSchema } from "./deck";

/**
 * A session's results, for downloading: the spreadsheet, the Q&A list and the printable
 * report are all made from this one document.
 *
 * Built from Postgres — what was persisted — rather than from the live state in Valkey,
 * which expires. For a session still running it is as complete as the answers saved so
 * far (the persistence queue runs a moment behind the room).
 */
const uuid = z.string().uuid();

export const sessionReportSchema = z
  .object({
    generatedAt: z.string().datetime(),
    session: z
      .object({
        sessionId: uuid,
        deckTitle: z.string(),
        joinCode: z.string(),
        status: z.enum(["lobby", "live", "ended"]),
        createdAt: z.string().datetime(),
        startedAt: z.string().datetime().nullable(),
        endedAt: z.string().datetime().nullable(),
      })
      .strict(),
    /** Everyone who joined, best first, ranked as everything ranks (ranking.ts). */
    participants: z.array(
      z
        .object({
          participantId: uuid,
          displayName: z.string().nullable(),
          rank: z.number().int().positive(),
          score: z.number().int().nonnegative(),
          answered: z.number().int().nonnegative(),
          correct: z.number().int().nonnegative(),
          /** Mean response time over the questions they answered, in ms. */
          averageMs: z.number().int().nonnegative().nullable(),
          /** Time taken over the answers that scored, in ms: what settles equal points. */
          scoredMs: z.number().int().nonnegative(),
          /** What their phone shows, to prove a prize is theirs. Null for those who joined before codes. */
          claimCode: claimCodeSchema.nullable(),
        })
        .strict(),
    ),
    /** Every slide that takes answers, in deck order, with what the room said. */
    slides: z.array(
      z
        .object({
          slideId: uuid,
          position: z.number().int().nonnegative(),
          type: slideTypeSchema,
          prompt: z.string(),
          isScored: z.boolean(),
          answered: z.number().int().nonnegative(),
          /** Scored slides only. */
          correct: z.number().int().nonnegative().nullable(),
          options: z.array(
            z.object({ optionId: uuid, label: z.string(), isCorrect: z.boolean(), count: z.number().int().nonnegative() }).strict(),
          ),
          /** Word clouds: each word and how many said it, most said first. */
          words: z.array(z.object({ word: z.string(), count: z.number().int().positive() }).strict()),
          /** Scales: the mean. */
          average: z.number().nullable(),
          /** Free text: every answer, in the order they arrived. */
          texts: z.array(z.string()),
        })
        .strict(),
    ),
    /** One per participant per slide they answered: what they said, readable. */
    answers: z.array(
      z
        .object({
          participantId: uuid,
          slideId: uuid,
          answer: z.string(),
          isCorrect: z.boolean().nullable(),
          points: z.number().int().nonnegative(),
          responseMs: z.number().int().nonnegative(),
        })
        .strict(),
    ),
    questions: z.array(
      z
        .object({
          body: z.string(),
          authorName: z.string().nullable(),
          status: z.enum(["pending", "approved", "answered", "rejected"]),
          upvoteCount: z.number().int().nonnegative(),
          createdAt: z.string().datetime(),
        })
        .strict(),
    ),
  })
  .strict();

export type SessionReport = z.infer<typeof sessionReportSchema>;

/**
 * A session's results as the presenter's screen shows them: the standings and how each
 * question went, without the answer-by-answer rows. Every plan sees these — they are
 * how a self-paced session (ADR 0008) is followed while it is open. The rows behind
 * them are the download, and stay with `sessionReportSchema`, where exports are gated.
 */
export const sessionResultsSchema = sessionReportSchema.omit({ answers: true, questions: true });
export type SessionResults = z.infer<typeof sessionResultsSchema>;
